// ============================================================
// Bolt — shared constants (single source of truth for client,
// server, resolution engine, and replay renderer).
// ============================================================
import type { WinCondition } from './series';
import type { TravelMode } from './travelMode';
import type { DeckConfig } from './pack';

/** Bumped whenever the wire protocol changes shape (§6.8 forward-compat).
 *  v5 (v9 BOLT-020): the version is ENFORCED two-way — clients declare it in
 *  create/join/rejoin; the server accepts N and N−1 (the skew window for
 *  web-updates-instantly vs iOS-updates-eventually) and answers anything else
 *  with a typed `protocol_version` error naming the requirement. */
export const PROTOCOL_VERSION = 11; // v29 BOLT-159: the READY gate + the hider (not the host) starting the round. The bump exists ONLY to make the flow capability-gateable: without it an older build declares 10 too, the room cannot tell it apart, and a client that never sends `set_ready` would hold the gate shut for ever - a beta full of half-updated phones that CANNOT START A GAME. At `capabilityFloor >= 11` the new flow applies; below it the room falls back to the host-starts path exactly as v28 shipped. v10 (v24) is the accepted N-1. // v24 BOLT-118: map-data cards — a client->server `map_features` upload + the seeker-only `featureCells` preview, and `minProtocol: 10` data cards gated by capabilityFloor so a v9 room is simply never dealt them. v9 (v20) is the accepted N-1. // v20 BOLT-096/098: additive seeker-only surfaces — freezeZone/freezeLocked (Freeze enforcement) + bordersPreview (faithful Borders preview). No new coordinate exposure. v8 (v19) is the accepted N-1.
/** Oldest client version the server still speaks (N−1 policy). */
export const MIN_PROTOCOL_VERSION = PROTOCOL_VERSION - 1;

/** v19 BOLT-087 — the readiness start-gate. A player is "location-ready" when
 *  their client reports `precise` auth with a fix no worse than
 *  READINESS_MAX_ACCURACY_M and no older than READINESS_MAX_FIX_AGE_MS. The
 *  server never sees a coordinate here — only the client's own report of its
 *  GPS state (auth level + accuracy/age), so the privacy invariant is untouched. */
export const READINESS_MAX_ACCURACY_M = 50;
export const READINESS_MAX_FIX_AGE_MS = 30_000;

/** v20 BOLT-096 — Freeze (holdup) enforcement. Casting Freeze drops a lock ring
 *  at the seeker's feet; if the timer expires while they're OUTSIDE it, their
 *  question cards lock until they return. Jake's pick was FORGIVING: a generous
 *  ring, and "outside" means outside by MORE than the live GPS accuracy (floored
 *  by FREEZE_MIN_BUFFER_M), so standing still never locks you — the lock bites
 *  real movement, not GPS drift. */
export const FREEZE_RING_FT = 55;
export const FREEZE_MIN_BUFFER_M = 8;

/** v16 BOLT-072: the horn's delivery window — the hider's client must ack the
 *  `airhorn` message inside it or the seeker's play is refunded (instance
 *  restored, no draw). Generous enough for a locked phone waking audio. */
export const WHISTLE_ACK_MS = 4000;

/** v17 BOLT-076 — the CAST-time volume bar on iOS (`outputVolume`, READ-only).
 *  Below it the phone stays silent and never acks, so the standing refund pays
 *  the seeker back; above it the horn plays at full app gain through the
 *  speaker override. Dropped 0.5 → 0.35: the gate is for near-mute, not for
 *  perfection. Web has no volume API to read and always plays + acks. */
export const WHISTLE_MIN_VOLUME = 0.35;

/** v29 BOLT-174 — THE WHISTLE's synth table, the ONE source for the web leg
 *  (`net/alert.ts playWhistle`). The iOS leg keeps its own literal table in
 *  `AlertEngine.swift` (no JS on the phone); `whistle-synth.test.ts` parses
 *  that Swift and asserts these numbers match, so the two can never drift
 *  silently. That parity mechanism is v17 BOLT-075's and is kept verbatim —
 *  only the waveform changed.
 *
 *  WHY THE BAND MOVED. The v17 table was `220 saw + 440 saw + 466 square`, and
 *  in the field it failed exactly as physics predicts: Jake cast it twice at
 *  close range and no seeker heard it, while it "spooked the hiders holding the
 *  phone". A smartphone micro-speaker sits in ~1 cc of enclosure and rolls off
 *  steeply below ~500 Hz, so 220 Hz was never radiated at all and 440 sat at the
 *  knee — the app was commanding full-scale amplitude at frequencies the
 *  transducer cannot put into the air. Worse, the layers summed to 1.2 before
 *  clipping, so the INAUDIBLE 220 Hz layer was eating ~29% of the headroom and
 *  forcing clipping to produce nothing.
 *
 *  Everything here is now 1500 Hz and up, where a phone speaker is near its most
 *  efficient AND the ear is most sensitive (the ear-canal quarter-wave resonance
 *  peaks around 3-4 kHz). Those two effects stack.
 *
 *  WHY IT WARBLES (Jake's design, and his reasoning): the carrier's pitch is
 *  swung ±`depthPercent` at `rateHz`. "A pure, unchanging 3 kHz sine forms
 *  standing waves in a room… a listener moving their head a couple inches can
 *  lose 15 dB. Wobbling the pitch smears those cancellation points across a
 *  small band, so no single frequency gets to disappear." That is a ROOM
 *  argument and Bolt is played outdoors, where the analogous effect is
 *  comb filtering from the ground bounce — smaller, but real, and the warble
 *  also stops the tone reading as background machinery.
 *
 *  WHY IT BURSTS, AND WHY IT IS NOT A BARE SINE. The card's job is
 *  LOCALIZATION, not detection — the codex says so: "No map effect. You
 *  localize by ear", "for when you KNOW they are close". Above ~1.5 kHz the ears
 *  lose interaural TIMING (the wavelength is shorter than a head, so phase
 *  wraps) and fall back on level differences and pinna spectral cues — which a
 *  single frequency gives the pinna nothing to shape. Pure-tone localization
 *  error peaks right around 3 kHz. So: `partials` gives the spectrum something
 *  to shape (including one at half the carrier, low enough to carry some timing
 *  information), and the tone is cut into BURSTS because onsets are what the ear
 *  actually points at. Bursts also make it far less punishing for the hider
 *  holding the phone, who is the one person guaranteed to be at arm's length.
 *
 *  The 20 ms fade exists so the tone does not open with a broadband click —
 *  Jake: "a click would let people 'hear' low tones they can't actually
 *  resolve — it skews any audibility test." */
export const WHISTLE_SYNTH = {
  /** The tone you hear. */
  centerHz: 3000,
  /** Pitch swing each way, as a percent of centre (±150 Hz at 3 kHz). */
  depthPercent: 5,
  /** Warble rate — how many times a second the pitch sweeps. */
  rateHz: 5,
  /** Fade in and out of every burst; kills the click without reading as a fade. */
  fadeSec: 0.02,
  /** One burst of tone, then silence, repeated. */
  burstSec: 0.22,
  gapSec: 0.12,
  bursts: 6,
  /**
   * [ratio of centreHz, gain]. Ratios, not absolute Hz, so retuning the carrier
   * moves the whole timbre together. Gains sum to exactly 1.0 — the v17 table
   * summed to 1.2 and clipped.
   */
  partials: [
    [1.0, 0.6],
    [0.5, 0.2],
    [1.5, 0.2],
  ],
} as const;

/** Total sounding length: bursts plus the gaps between them. */
export const WHISTLE_DURATION_SEC =
  WHISTLE_SYNTH.bursts * WHISTLE_SYNTH.burstSec + (WHISTLE_SYNTH.bursts - 1) * WHISTLE_SYNTH.gapSec;

/**
 * Simulated-GPS anchor / play origin (spec §12.2). All fixtures and the
 * initial map camera center here. Real Craig Ranch North, McKinney TX.
 */
export const ORIGIN = { lat: 33.15636450764182, lng: -96.72670005721211 } as const;

export const FEET_PER_METER = 3.280839895;
export const METERS_PER_FOOT = 0.3048;
export const ft = (feet: number) => feet * METERS_PER_FOOT; // → meters
export const SQ_METERS_PER_SQ_MILE = 2_589_988.110336;

/** Brand palette — the only colours in the system (§11.1). */
export const COLORS = {
  slate: '#14171A',
  graphite: '#1B1F23',
  graphite2: '#22272C',
  signal: '#E8451B',
  signalDeep: '#B5320E',
  hazard: '#F5C842',
  moss: '#5C7A6E',
  bone: '#F5F1E6',
  portal: '#4CAF6A', // v13 BOLT-040: was a scattered literal on both platforms
} as const;

/** v13 BOLT-040: the non-color style tokens gen:swift mirrors into
 *  BrandTokens.swift (radii from tokens.css; muted-bone opacity steps). */
export const STYLE = {
  radius: 10,
  radiusSm: 6,
  boneSoftAlpha: 0.7,
  boneMuteAlpha: 0.45,
  // v14 BOLT-043 — the mic button: near-transparent at rest (the map reads
  // through it), ~2× + glow while recording (state reads AROUND the thumb).
  // Consumed by round.css (.voice-mic) AND the generated BrandTokens.swift.
  micIdleAlpha: 0.18,
  micPressedScale: 2,
} as const;

/** Default round settings — server-authoritative, tunable later (§4.3).
 *  v2 adds the hider-deck economy, veto window, curse durations, and penalties.
 *  A `create` message may override any of these (used for fast test windows). */
/**
 * v29 BOLT-163 (Jake): "Veto window should stay at 30s but we need to change
 * the to allow changing in increments of 5s."
 *
 * 15 s was too coarse to tune: from the 30 s default the only reachable
 * neighbours were 15 s and 45 s, and the interesting range for a veto is the
 * few seconds either side of it. ONE constant, shared by both platforms'
 * steppers, so they cannot disagree about what a tap is worth.
 */
export const VETO_STEP_MS = 5_000;

export const DEFAULT_SETTINGS = {
  headStartMs: 15 * 60_000, // 15 min head start
  maxRoundMs: 45 * 60_000, // 45 min cap
  foundRadiusFt: 15, // proximity catch radius
  hidingRadiusFt: 75, // honour-system hiding zone (hider-only)
  thermoWindowMs: 5 * 60_000, // thermometer walk window

  // ---- v2 ----
  // v29 BOLT-168 (Jake: "Raise hand cap to 10"). It was 5, and his retune puts
  // every Nearest card and the Whistle at cost 5 - so ONE of them filled the
  // hand from empty, and a hider holding anything at all was paid and then
  // immediately forced to discard the payment (`recomputeOverflow` →
  // `mustDiscard`). Raising the cap keeps his costs and makes a big payout
  // actually keepable. Watch the first round played on it: the cap is the
  // hider's spending pressure, and this is the number that sets it.
  handCap: 10, // hider hand cap
  vetoWindowMs: 30_000, // per-question veto window (0 = instant, = MVP)

  // v18 BOLT-081 — ONE length for every timed hider card (Jake: "all
  // time-based [cards] use the same length, defaulted to 2, but exposed as a
  // customizable game setting"). It replaced curseHoldupMs / curseSnailMs /
  // curseBabelMs: three numbers to tune, three settings rows and three codex
  // chips, with a fourth about to be born with Flying Blind. Net changes at
  // this default: Freeze 1:00 → 2:00, Silence 1:00 → 2:00, Babel 5:00 → 2:00.
  // The old keys are still ACCEPTED on the wire for one version (ignored) so a
  // v6 client's saved settings cannot break a room — see mergeSettings.
  blockDurationMs: 1 * 60_000,
  penaltyGraceMs: 30_000, // grace before a boundary/zone penalty accrues
  penaltyStepMs: 30_000, // accrual cadence
  penaltyPerStepMin: 5, // minutes deducted per step

  // ---- v23 BOLT-123: how the players are getting around ----
  // Additive optional on the wire (no protocol bump). It is the barrier preset
  // for the auto-boundary, and it is PERSISTED with the game record so we can
  // later see how people actually play (e.g. mode-scaled card distances).
  // NB: a LITERAL, not `DEFAULT_TRAVEL_MODE`. ts-json-schema-generator cannot
  // resolve an imported identifier inside an `as` expression, and gen:swift
  // runs through it. `travelMode.test.ts` asserts the two never drift.
  travelMode: 'foot' as TravelMode,

  // ---- v4: the series ----
  roundsPerTeam: 1, // hides per team; total rounds = ×2 (1 ⇒ today's single round)
  winCondition: 'best' as WinCondition, // best | cumulative | mostRounds
  deckConfig: {} as DeckConfig, // per-card enable/count overrides (empty ⇒ catalog defaults)

  // Curse of the Portal (trap)
  portalSizeFt: 50, // entry/exit box edge length
  portalMinExitFt: 500, // exit ≥ this far from the entry (anti-no-op floor)
  portalMaxExitFt: 1000, // exit ≤ this far from the entry (v8 BOLT-006: hider-placed exit band)
  portalKeepoutFt: 250, // entry center must be ≥ this from BOTH live pins

  // ---- v5: real-GPS hardening (no-op under simulation, acc 0/undefined) ----
  foundAccuracyMarginFt: 25, // extra Found tolerance to absorb GPS accuracy (capped)
  minFixAccuracyM: 35, // boundary/zone penalties ignore fixes worse than this

  // ---- v3: Photo cards ----
  // v5.5 §7: shortened 5:00 → 2:00 so the seeker's flush stays honest while still
  // leaving time to reach a local target and frame the shot. Host-tunable.
  photoWindowMs: 2 * 60_000, // time the hider has to upload after the veto window
  photoPenaltyRadiusFt: 250, // failure penalty radar radius (Z ∩ disk(H, r))
  photoMaxBytes: 2_000_000, // cap on a decoded upload (client sends a ≤800px JPEG; server re-encodes)

  // ---- v11 §2D: voice notes (walkie-talkie messages, not live PTT) ----
  voiceMaxBytes: 1_500_000, // cap on a decoded voice upload (10 s AAC ≈ 100 KB)
  voiceMaxSeconds: 10, // duration cap (client stops the recorder; server re-checks M4A)

  deckSeed: 0, // >0 → deterministic hider-deck shuffle (tests)
};

export type Settings = typeof DEFAULT_SETTINGS;

/** v19 BOLT-084 — the Practice preset. A single-player demo round against a
 *  server bot, tuned to show the whole loop in a couple of minutes. Jake's
 *  numbers: veto ~10s, every timed card ~30s. Expressed as an override of
 *  DEFAULT_SETTINGS (never a fresh literal), so a new setting inherits its
 *  default here unless Practice deliberately tunes it. */
/** v22 BOLT-113: the editable Found distance, in feet. The default
 *  (`DEFAULT_SETTINGS.foundRadiusFt`) is the real-life "close enough to see
 *  them" radius; the range is what a human can sensibly tune around it.
 *  Practice overrides it far larger (see PRACTICE_SETTINGS below). */
/** v22 BOLT-116: at or below this, the host sees a non-blocking caution before
 *  start. GPS play drains fast, and finding out mid-round is the bad outcome.
 *  A WARNING, never a wall: it mirrors the readiness soft-gate posture. */
export const LOW_BATTERY_PCT = 25;

export const FOUND_RADIUS_FT_MIN = 10;
export const FOUND_RADIUS_FT_MAX = 300;
export const FOUND_RADIUS_FT_STEP = 5;
export const PRACTICE_FOUND_RADIUS_FT = 150;

export const PRACTICE_SETTINGS: Partial<Settings> = {
  vetoWindowMs: 10_000, // Jake: veto ~10s in Practice
  blockDurationMs: 30_000, // Jake: every timed card ~30s in Practice
  headStartMs: 5_000, // short — the bot hider is already placed
  maxRoundMs: 15 * 60_000, // a believable but compact demo round
  roundsPerTeam: 1, // single round (the legacy, no-first-hider path)
  // v22 BOLT-113: in a solo bot practice you cannot physically walk up to and
  // SEE the hider, so the real-life ~15 ft catch radius is unwinnable and the
  // loop never completes. ~150 ft lets you actually finish a practice round.
  foundRadiusFt: PRACTICE_FOUND_RADIUS_FT,
};


/** Radar radii (ft) and Thermometer minimum displacements (ft) (§4.4). */
export const RADAR_RADII_FT = [1000, 500, 250, 100] as const;
export const THERMO_MINS_FT = [250] as const; // v14 BOLT-044: one distance for every copy

/** Douglas–Peucker tolerance (degrees) + hard vertex cap for Z (§Phase 1). */
export const SIMPLIFY_TOLERANCE_DEG = 0.00002; // ~2 m
export const MAX_ZONE_VERTICES = 2000;

/** Thermometer tie epsilon — |d(H,Pend) − d(H,Pstart)| below this ⇒ no clip. */
export const THERMO_TIE_EPSILON_M = 0.5;

export const REPLAY_TARGET_SECONDS_DEFAULT = 20;
